import { ClientsMetadata } from 'apiConnector/secondary-market/batch/base';
import { OtcClientSelector } from 'components/secondaryMarket/shared/components';
import { ClientSelector } from 'components/secondaryMarket/shared/components/clientSelectors';
import { ISalesCounterpart } from 'models/ISalesCounterpart';
import { useCallback, useMemo } from 'react';
import { useEditableInfo } from 'routes/secondary-market-batch/grid/hooks/use-editable-info';
import { asSecondaryCellEditorProps, SecondaryCellProps } from 'routes/secondary-market-batch/grid/types';
import { isPrintableKey } from 'utils/aggrid';

const defaultMetadata: ClientsMetadata = { items: [], restricted: false };
export function ClientCellEditor({
  value,
  colDef,
  node,
  data,
  api,
  column,
  ...props
}: SecondaryCellProps<ISalesCounterpart>) {
  const editorProps = useMemo(() => asSecondaryCellEditorProps(props), [props]);
  const onClientChanged = useCallback(
    (client?: ISalesCounterpart | null) => {
      node.setDataValue(colDef.field!, client ?? null);
      editorProps.stopEditing?.();
    },
    [node, colDef.field, editorProps],
  );

  const onClientClear = useCallback(() => {
    onClientChanged(null);
  }, [onClientChanged]);

  const metadata = colDef.context.metadataId
    ? ((data?.metadata[colDef.context.metadataId] as ClientsMetadata | undefined) ?? defaultMetadata)
    : defaultMetadata;

  const { isEditable, isEditing } = useEditableInfo({
    rowIndex: editorProps.rowIndex,
    column,
    node,
    api,
  });
  const stopEditingOnClose = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        editorProps.stopEditing?.();
      }
    },
    [editorProps],
  );
  return !metadata.restricted ? (
    <ClientSelector
      isReadOnly={props.type === 'renderer'}
      autoOpen={isEditing}
      onClientChanged={onClientChanged}
      selectedClient={value ?? undefined}
      loadingStatus="success"
      onClientClear={onClientClear}
      productClients={metadata.items}
      defaultSearchedValue={isPrintableKey(editorProps.eventKey) ? editorProps.eventKey : undefined}
      isDisabled={!isEditable}
      onOpenChange={stopEditingOnClose}
    />
  ) : (
    <OtcClientSelector
      isReadonly={props.type === 'renderer'}
      isDisabled={!isEditable}
      className="flex-grow-1 justify-content-start min-width-0"
      otcClientLoadingStatus="success"
      productOtcClients={metadata.items}
      selectedClientEliotCode={value?.eliotCode}
      onClientChanged={onClientChanged}
      onClientClear={onClientClear}
      onOpenChange={stopEditingOnClose}
    />
  );
}
